import { useState, useEffect, useCallback } from 'react';

/**
 * PWAの更新状態を管理するカスタムフック
 *
 * index.tsx で serviceWorkerRegistration.register() が既に呼ばれているため、
 * このフックは SW の登録を再度行わず、既存の登録を監視するだけにする。
 *
 * 更新フロー:
 * 1. 新しい SW がバックグラウンドでインストールされると waiting 状態になる
 * 2. isUpdateAvailable が true になり、サイドバーに「最新版に更新」ボタンが表示される
 * 3. ユーザーがボタンを押すと applyUpdate() を呼ぶ
 * 4. 待機中の SW に SKIP_WAITING メッセージを送信
 * 5. SW が skipWaiting() → activate → clients.claim()
 * 6. controllerchange イベントでページをリロード
 * 7. IndexedDB のデータはそのまま保持される
 */
export function usePWAUpdate() {
  const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isChecking, setIsChecking] = useState(false);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const handleWaitingWorker = (sw: ServiceWorker) => {
      setWaitingWorker(sw);
      setIsUpdateAvailable(true);
      console.log('[usePWAUpdate] New version is ready and waiting');
    };

    // 既存の登録から waiting 中の SW を検出
    navigator.serviceWorker.ready.then((registration) => {
      // ページロード時点で既に待機中の SW がある場合（前回の更新が未適用）
      if (registration.waiting) {
        handleWaitingWorker(registration.waiting);
      }

      // 今後の更新を検出するリスナー
      registration.addEventListener('updatefound', () => {
        const installingWorker = registration.installing;
        if (!installingWorker) return;

        installingWorker.addEventListener('statechange', () => {
          if (
            installingWorker.state === 'installed' &&
            navigator.serviceWorker.controller
          ) {
            // 既存のコントローラーがある = アップデート（初回インストールではない）
            handleWaitingWorker(installingWorker);
          }
        });
      });
    });

    // controllerchange: 新しい SW がアクティブになったらリロード
    const handleControllerChange = () => {
      console.log('[usePWAUpdate] Controller changed, reloading to apply update...');
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);

    return () => {
      navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
    };
  }, []);

  /**
   * 更新を適用する
   * 待機中の SW に SKIP_WAITING を送信 → activate → controllerchange → reload
   * IndexedDB・localStorage のデータは保持される
   */
  const applyUpdate = useCallback(() => {
    if (!waitingWorker) {
      console.warn('[usePWAUpdate] No waiting worker found');
      return;
    }
    setIsUpdating(true);
    waitingWorker.postMessage({ type: 'SKIP_WAITING' });
  }, [waitingWorker]);

  /**
   * 手動で更新チェックを行う
   * registration.update() でサーバーから最新の SW を確認する
   */
  const checkForUpdate = useCallback(async () => {
    if (!('serviceWorker' in navigator) || isChecking) return;
    setIsChecking(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      await registration.update();
    } catch (e) {
      console.warn('[usePWAUpdate] Update check failed:', e);
    } finally {
      setIsChecking(false);
    }
  }, [isChecking]);

  return { isUpdateAvailable, isUpdating, isChecking, applyUpdate, checkForUpdate };
}
